import React, { forwardRef, useRef } from 'react';
import CalendarMonth from "./CalendarMonth";
import styled from "styled-components";
import useTranslation from "../../hooks/useTranslation";

const CalendarWrapper = styled.div`
  position: absolute;
  background-color: ${({ theme }) => theme.dropdown.background};
  top:2.75rem;
  right: ${({ $align }) => $align === 'right' ? '0' : ''};
  padding-top: 0.5rem;
  border-radius: 1rem;
  border: 1px solid ${({ theme }) => theme.dropdown.border};
  z-index: 1001;
  display: ${({ $isVisible }) => $isVisible ? 'block' : 'none'};
`

const CalendarTable = styled.table`
  margin: 0rem 0.5rem;
  padding: 0.5rem;
`

const CalendarCell = styled.td`
  text-align: center;
  width: 45px;
  font-size: 1rem;

  &:not(.empty):not(.disabled):hover {
    background-color: ${({ theme }) => theme.dropdown.selectedBackground};
    color: ${({ theme }) => theme.dropdown.selectedFont};
    border-radius: 25px;
  }

  &.selected {
    background-color: ${({ theme }) => theme.calendar.selectedBackground};
    border-radius: 25px;
  }

  &.empty {
    pointer-events: none;
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.35;
  }

  & > button {
    background-color: transparent;
    color: inherit;
    border: none;
    margin: 0;
    padding: 0;
    width: 100%;
    cursor: pointer;
  }
`

const CalendarHeader = styled.th`
  text-align: center;
  width: 45px;
  font-size: 1rem;
`


const Calendar = forwardRef(({ isVisible, date, setDate, onSelect, $align = 'right', min, max, yearPast, yearFuture }, ref) => {
  const tableRef = useRef(null);
  const { t } = useTranslation();

  const handleMonthChange = (month) => {
    setDate((prevDate) => new Date(prevDate.getFullYear(), month, prevDate.getDate()));
  };

  const handleYearChange = (year) => {
    setDate((prevDate) => new Date(year, prevDate.getMonth(), prevDate.getDate()));
  };

  const handleDaySelect = (e, day) => {
    e.preventDefault();
    const selectedDate = new Date(date.getFullYear(), date.getMonth(), day);
    onSelect(selectedDate);
    setDate(selectedDate);
  };

  const handleButtonKeyDown = (e, day) => {
    const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    let newDay = null;

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      newDay = Math.min(day + 1, daysInMonth);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      newDay = Math.max(day - 1, 1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      newDay = Math.min(day + 7, daysInMonth);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      newDay = Math.max(day - 7, 1);
    }

    if (newDay !== null && tableRef.current) {
      const btn = tableRef.current.querySelector(`button[data-day="${newDay}"]`);
      if (btn) btn.focus();
    }
  };

  const getCellClass = (day) => {
    if (day === null) return 'empty';
    const parts = [];
    if (day === date.getDate()) parts.push('selected');
    if (isDisabled(day, date, min, max)) parts.push('disabled');
    return parts.join(' ') || undefined;
  };

  return (
    <CalendarWrapper $align={$align} $isVisible={isVisible} ref={ref}>
      <CalendarMonth
        date={date}
        onMonthChange={handleMonthChange}
        onYearChange={handleYearChange}
        yearPast={yearPast}
        yearFuture={yearFuture}
      />
      <CalendarTable>
        <thead>
          <tr>
            <CalendarHeader>{t('week.sun')}</CalendarHeader>
            <CalendarHeader>{t('week.mon')}</CalendarHeader>
            <CalendarHeader>{t('week.tue')}</CalendarHeader>
            <CalendarHeader>{t('week.wed')}</CalendarHeader>
            <CalendarHeader>{t('week.thu')}</CalendarHeader>
            <CalendarHeader>{t('week.fri')}</CalendarHeader>
            <CalendarHeader>{t('week.sat')}</CalendarHeader>
          </tr>
        </thead>
        <tbody ref={tableRef}>
          {getCalendarRows(date).map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((day, dayIndex) => (
                <CalendarCell
                  key={dayIndex}
                  className={getCellClass(day)}
                >
                  <button
                    data-day={day}
                    tabIndex={day === date.getDate() ? 0 : -1}
                    onClick={(e) => day !== null && handleDaySelect(e, day)}
                    onKeyDown={(e) => day !== null && handleButtonKeyDown(e, day)}
                  >
                    {day}
                  </button>
                </CalendarCell>
              ))}
            </tr>
          ))}
        </tbody>
      </CalendarTable>
    </CalendarWrapper>
  );
});

function isDisabled(day, date, min, max) {
  if (!min && !max) return false;
  const d = new Date(date.getFullYear(), date.getMonth(), day);
  if (min && d < min) return true;
  if (max && d > max) return true;
  return false;
}

function getCalendarRows(date) {
  if (!(date instanceof Date) || isNaN(date)) return [['1', '2', '3']];
  const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
  const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  const startDate = new Date(monthStart);
  startDate.setDate(startDate.getDate() - startDate.getDay()); // start on Sunday
  const endDate = new Date(monthEnd);
  endDate.setDate(endDate.getDate() + (6 - endDate.getDay())); // end on Saturday

  const rows = [];
  let row = [];
  let currentDate = startDate;
  while (currentDate <= endDate) {
    if (row.length === 7) {
      rows.push(row);
      row = [];
    }
    if (currentDate < monthStart || currentDate > monthEnd) {
      row.push(null);
    } else {
      row.push(currentDate.getDate());
    }
    currentDate = new Date(currentDate);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  if (row.length > 0) {
    rows.push(row);
  }
  return rows;
}


export default Calendar;
